export const TASK_STATUSES = ['pending', 'ongoing', 'waiting', 'completed', 'cancelled'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export interface TaskAssignee {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

export interface Task {
  id: string;
  /** Speakable, per desk: S-142, B-87. */
  reference: string;
  deskId: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  tag: string | null;
  assignee: TaskAssignee | null;
  /** Set only while status is waiting. */
  waitingOn: string | null;
  remindAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  statusChangedAt: string;
  version: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Relative to whoever is asking, and empty unless they are the assignee — an event
   * on someone else's card is their notification, not yours.
   */
  unread: { isNew: boolean; hasActivity: boolean; count: number };
  /** How much story the card has accumulated, shown on its face. */
  noteCount: number;
}

export interface BoardColumn {
  status: TaskStatus;
  total: number;
  unread: number;
  hasMore: boolean;
  tasks: Task[];
}

/** What one column is filtered by. Empty object means unfiltered. */
export interface ColumnFilter {
  /*
    `| undefined` on every field, because tsconfig sets exactOptionalPropertyTypes.
    Under that flag an optional property may be ABSENT but not present-and-undefined,
    and "clear this one filter" is naturally written as `{ ...draft, tag: undefined }`.
  */
  priority?: TaskPriority[] | undefined;
  tag?: string | undefined;
  overdue?: boolean | undefined;
}

export interface Notification {
  id: string;
  taskId: string;
  reference: string;
  taskTitle: string;
  taskStatus: TaskStatus;
  eventType: string;
  actorType: 'user' | 'agent' | 'system' | 'admin';
  actorName: string | null;
  actorDeskId: string | null;
  fromStatus: TaskStatus | null;
  toStatus: TaskStatus | null;
  body: string | null;
  createdAt: string;
  unseen: boolean;
}

export interface NotificationPage {
  items: Notification[];
  nextCursor: string | null;
  unseenCount: number;
  seenAt: string | null;
}

export interface BoardColumnPage {
  status: TaskStatus;
  /** Across the whole column, never the page — see the note in repo.getBoardColumn. */
  total: number;
  unread: number;
  items: Task[];
  /** Null on the last page. Opaque: produced by the server, never built here. */
  nextCursor: string | null;
}

export interface Board {
  columns: BoardColumn[];
  /** Newest updated_at seen. Passed back after a dropped socket to fetch the gap. */
  watermark: string | null;
}

export interface CurrentUser {
  id: string;
  email: string;
  role: 'employee' | 'admin';
  timezone: string;
  isAdmin: boolean;
}

export interface TaskEvent {
  id: string;
  eventType: string;
  actorType: 'user' | 'agent' | 'system' | 'admin';
  actorName: string | null;
  actorDeskId: string | null;
  source: string;
  fromStatus: TaskStatus | null;
  toStatus: TaskStatus | null;
  body: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: 'employee' | 'admin';
  status: 'invited' | 'active' | 'suspended';
  timezone: string;
  telegramUserId: string | null;
  telegramUsername: string | null;
  /** False until they have signed in with Google at least once. */
  hasSignedIn: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  openTasks: number;
}

export interface NewUser {
  email: string;
  fullName: string;
  telegramUserId?: string | null;
  role?: 'employee' | 'admin';
}
